import {ImageSubmission} from "../../../../api/ApiTypes.ts";
import React, {useEffect, useState} from "react";
import {FileDownloadType} from "../../../FileViewer.tsx";
import {base64ToArrayBuffer} from "../../../../filehelper/fileutils.ts";
import {AlertCircle, Loader} from "lucide-react";
import {useFileApi} from "../../../../context/ApiContext.tsx";
async function retryOn404<T>(
    downloadFn: () => Promise<T>,
    retries: number = 4,
    delay: number = 250
): Promise<T> {
    try {
        // Attempt the download
        return await downloadFn();
    } catch (error: any) {
        // Check if it's a network error with a 404 status and we still have retries left
        if (error.response?.status === 400 && retries > 0) {
            console.warn(`File not found, retrying in ${delay}ms... (${retries} retries left)`);

            // Wait for the specified delay
            await new Promise(resolve => setTimeout(resolve, delay));

            // Recurse with one less retry and a slightly longer delay (exponential backoff)
            return retryOn404(downloadFn, retries - 1, delay * 2);
        } else {
            // If it's another error or we're out of retries, throw the original error
            throw error;
        }
    }
}

const ImageDisplayRenderer: React.FC<{ image: ImageSubmission }> = ({image}) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const fileApi = useFileApi();

    useEffect(() => {
        let isMounted = true;

        const loadImage = async () => {
            setIsLoading(true);
            setError(null);
            try {

                const downloadFn = () => fileApi.downloadFile(
                    image.generalFileId,
                    FileDownloadType.GENERAL_FILE
                );

                const response = await retryOn404(downloadFn);

                const blob = new Blob([base64ToArrayBuffer(response.data)], {
                    type: response.contentType || 'image/jpeg'
                });
                const url = window.URL.createObjectURL(blob);
                if (isMounted) {
                    setImageUrl(url);
                }
            } catch (err) {
                console.error('Failed to load image:', err);
                if (isMounted) {
                    setError('Could not load image.');
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadImage();

        return () => {
            isMounted = false;
            if (imageUrl) {
                window.URL.revokeObjectURL(imageUrl);
            }
        };
    }, [image.generalFileId]); // Re-run when the image ID changes

    if (isLoading) {
        return (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                <Loader className="animate-spin h-8 w-8 text-gray-500"/>
            </div>
        );
    }

    if (error || !imageUrl) {
        return (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <AlertCircle className="h-8 w-8 text-red-500"/>
            </div>
        );
    }

    return (
        <img
            src={imageUrl}
            alt={image.fileName}
            className="absolute inset-0 w-full h-full object-contain"
        />
    );
}

export const ImageDisplay = React.memo(ImageDisplayRenderer);
